import { useState, useEffect } from "react"
import { Bookmark } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/context/AuthContext"
import { addBookmark, removeBookmark, isBookmarked } from "@/lib/db"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

interface BookmarkButtonProps {
  postId: string
  variant?: "default" | "ghost" | "outline"
  size?: "default" | "sm" | "lg" | "icon"
  showLabel?: boolean
  className?: string
}

export function BookmarkButton({ postId, variant = "ghost", size = "default", showLabel = false, className }: BookmarkButtonProps) {
  const { user, firebaseUser } = useAuth()
  const [bookmarked, setBookmarked] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!firebaseUser) {
      setBookmarked(false)
      return
    }

    const userId = firebaseUser.id
    async function checkBookmark() {
      try {
        const result = await isBookmarked(userId, postId)
        setBookmarked(result)
      } catch (error) {
        console.error("Error checking bookmark:", error)
      }
    }

    checkBookmark()
  }, [firebaseUser, postId])

  const handleToggleBookmark = async () => {
    if (!firebaseUser) {
      toast.error("Please log in to bookmark posts")
      return
    }

    setLoading(true)
    try {
      if (bookmarked) {
        await removeBookmark(firebaseUser.id, postId)
        setBookmarked(false)
        toast.success("Removed from bookmarks")
      } else {
        await addBookmark(firebaseUser.id, postId)
        setBookmarked(true)
        toast.success("Added to bookmarks")
      }
    } catch (error) {
      console.error("Error toggling bookmark:", error)
      toast.error("Failed to update bookmark")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleToggleBookmark}
      disabled={loading}
      className={cn(className)}
      aria-label={bookmarked ? "Remove bookmark" : "Add bookmark"}
    >
      <Bookmark className={cn("h-4 w-4", bookmarked && "fill-current", showLabel && "mr-2")} />
      {showLabel && (bookmarked ? "Bookmarked" : "Bookmark")}
    </Button>
  )
}

